/** Backend status: 0=DRAFT, 1=ASSIGNED, 2=UNASSIGNED, 3=ARCHIVE */
export enum ExamStatus {
  DRAFT = 0,
  ASSIGNED = 1,
  UNASSIGNED = 2,
  ARCHIVE = 3,
}

export interface ExamRelatedEducation {
  id: string;
  name: string;
}

/** GET /api/admin/exams list item - backend response shape */
export interface Exam {
  id: string;
  name: string;
  description?: string;
  passingScore: number;
  durationMinutes: number;
  /** Bu sınav için maksimum başarısız deneme sayısı; yoksa domain default kullanılır. */
  maxExamFailureCount?: number | null;
  /** Kullanıcı önceki soruya dönebilsin mi? */
  canGoPreviousQuestions?: boolean;
  status: number;
  createdAt: string;
  updatedAt: string;
  questions: ExamResDtoQuestion[];
  relatedEducations: ExamRelatedEducation[];
  totalAttempts: number;
  successRate: number | null;
  participantCount: number;
  passedCount: number;
  failedCount: number;
}

export interface ExamFilters {
  search?: string;
  status?: number;
  sortBy?: "createdAt" | "name" | "successRate" | "passingScore";
  sortOrder?: "asc" | "desc";
  createdAtStart?: string;
  createdAtEnd?: string;
  educationIds?: string[];
  minPassingScore?: number;
  maxPassingScore?: number;
  minSuccessRate?: number;
  maxSuccessRate?: number;
}

// --- POST /api/admin/exams (CreateExamDto) ---

export interface CreateExamQuestionOptionDto {
  optionText: string;
  order?: number;
  isCorrect: boolean;
}

export interface CreateExamQuestionDto {
  questionText: string;
  order?: number;
  allowMultipleSelection: boolean;
  imageFileID?: string;
  options: CreateExamQuestionOptionDto[];
}

export interface CreateExamDto {
  name: string;
  description?: string;
  passingScore: number;
  durationMinutes: number;
  /** Bu sınav için özel deneme limiti. Yoksa domain default kullanılır. */
  maxExamFailureCount?: number | null;
  status: number; // 0=DRAFT, 1=ACTIVE
  questions: CreateExamQuestionDto[];
  /** Kullanıcı önceki soruya dönebilsin mi? */
  canGoPreviousQuestions?: boolean;
}

// --- GET /api/admin/exams (backend list & detail) ---

export interface ExamResDtoQuestionOption {
  id: string;
  questionID: string;
  optionText: string;
  order: number;
  isCorrect: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Backend'den gelen soru görseli (presigned URL vb.) */
export interface ExamResDtoQuestionImage {
  id: string;
  downloadUrl: string;
  filename?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  sizeFormatted?: string;
  expiresAt?: string;
  expiresIn?: number;
}

export interface ExamResDtoQuestion {
  id: string;
  examID: string;
  questionText: string;
  order: number;
  allowMultipleSelection: boolean;
  imageFileID: string | null;
  /** Presigned URL (legacy / fallback) */
  imageUrl?: string;
  /** Görsel detayı ve downloadUrl (ön izleme için kullan) */
  image?: ExamResDtoQuestionImage;
  options: ExamResDtoQuestionOption[];
  createdAt: string;
  updatedAt: string;
}

/** Backend list/detail item (same shape as Exam) */
export type ExamResDto = Exam;

/** Frontend detay modeli (drawer için) */
export type ExamDetail = Exam

// --- Exam Attendees (GET /api/admin/exams/{examId}/attempts) ---

/** Backend attempt status: 0=NOT_PARTICIPATED, 1=PASSED, 2=FAILED */
export enum ExamAttemptStatus {
  NOT_PARTICIPATED = 0,
  PASSED = 1,
  FAILED = 2,
}

export type ExamAttendeeStatusLabel = "gecti" | "kaldi" | "katilmadi" | "basladi";

export interface ExamAttendee {
  id: string;
  name: string;
  title: string;
  department: string;
  /** Bu satırın ait olduğu eğitim (aynı kullanıcı birden fazla eğitime kayıtlı olabileceği için her (user+education) ayrı satır) */
  educationID: string | null;
  educationName: string;
  score: number | null;
  status: ExamAttendeeStatusLabel;
  /** Backend raw status (0/1/2) */
  rawStatus: ExamAttemptStatus;
}

/** İstatistik verileri (response root alanları) */
export interface ExamAttendeeStats {
  count: number;
  successRate: number;
  /** Ortalama tamamlama süresi (saniye) */
  averageCompletionTime: number;
  notParticipatedCount: number;
}

export interface GetExamAttendeesParams {
  search?: string;
  /** 0=Katılmadı, 1=Geçti, 2=Kaldı */
  status?: number;
  departmentIds?: string[];
  sortBy?: "name" | "score";
  sortOrder?: "asc" | "desc";
}

/** Backend exam-attempt answer DTO (GET /api/admin/exams/exam-attempts/{attemptId}/answers) */
export interface ExamAttemptAnswerResDto {
  id: string;
  questionID: string;
  questionText: string;
  /** Kullanıcının seçtiği seçeneklerin metinleri */
  selectedOptions: string[];
  /** Doğru seçeneklerin metinleri */
  correctOptions: string[];
  /** Kullanıcının cevabı doğru mu */
  isAnswerCorrect: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Backend exam-attempt detail DTO */
export interface ExamAttemptDetailResDto {
  id: string;
  userID: string;
  user?: {
    firstName?: string;
    lastName?: string;
    title?: string;
    department?: { id: string; name: string };
  };
  startedAt?: string;
  finishedAt?: string;
  score: number;
  status: number; // 0=NOT_PARTICIPATED, 1=PASSED, 2=FAILED
  answers: ExamAttemptAnswerResDto[];
  createdAt: string;
  updatedAt: string;
}
